import { Calendar, Car, ChevronRight, MapPin, User, Video } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Session } from "@/types";
import { formatUserName } from "@/utils/userName";
import { LiveStreamStatusBadge } from "@/components/liveStream/LiveStreamStatusBadge";

interface SessionCardProps {
  session: Session;
  /** Show ownership clues ("You" chip + driver name). Passed on the All Sessions scope only. */
  showOwner?: boolean;
  /** Id of the signed-in user; rows they own get the "You" chip. Undefined when logged out. */
  currentUserId?: string;
}

const ACTIVE_STREAM_STATUSES = ["starting", "live", "stopping"] as const;

function isActiveStreamStatus(
  status: Session["liveStreamDisplayStatus"],
): status is (typeof ACTIVE_STREAM_STATUSES)[number] {
  return (
    status != null &&
    (ACTIVE_STREAM_STATUSES as readonly string[]).includes(status)
  );
}

export default function SessionCard({
  session,
  showOwner = false,
  currentUserId,
}: SessionCardProps) {
  const { t, i18n } = useTranslation(["pages", "common"]);

  const isMine =
    currentUserId !== undefined && session.userId === currentUserId;
  // Empty when both name halves are blank; the driver item is then omitted
  // rather than showing an empty slot or falling back to the email.
  const driverName = formatUserName(session.user);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const vehicleDisplayName =
    session.vehicle.name ||
    `${session.vehicle.year} ${session.vehicle.maker} ${session.vehicle.model}`;

  return (
    <Link
      to={`/sessions/${session.id}`}
      className="block bg-white rounded-lg border border-secondary-200 min-h-11 px-3 py-2.5 sm:px-5 sm:py-3.5 transition-colors hover:border-secondary-300"
    >
      {/* Row 1: Title + live badge + chevron */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-sm sm:text-base font-semibold text-secondary-900 truncate">
          {session.title || session.event.track.name}
        </span>
        {showOwner && isMine && (
          <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-blue-200 bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800">
            <User className="h-3 w-3" aria-hidden="true" />
            {t("pages:sessions.sessionCard.you")}
          </span>
        )}
        {isActiveStreamStatus(session.liveStreamDisplayStatus) && (
          <span className="flex-shrink-0">
            <LiveStreamStatusBadge status={session.liveStreamDisplayStatus} />
          </span>
        )}
        <ChevronRight className="h-4 w-4 text-secondary-300 flex-shrink-0 sm:hidden" />
      </div>

      {/* Row 2: Inline metadata */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs sm:text-sm text-secondary-500">
        <span className="flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
          {formatDate(session.createdAt)}
        </span>
        {showOwner && driverName && (
          <>
            <span className="text-secondary-400">·</span>
            <span
              className={`truncate max-w-xs ${isMine ? "font-medium text-primary-600" : ""}`}
            >
              {driverName}
            </span>
          </>
        )}
        <span className="flex items-center gap-1 min-w-0">
          <Car className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
          <span className="truncate">{vehicleDisplayName}</span>
        </span>
        <span className="flex items-center gap-1 min-w-0">
          <MapPin className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
          <span className="truncate">{session.event.track.name}</span>
        </span>
        {(session.videoCount ?? 0) > 0 && (
          <span
            role="img"
            className="flex items-center gap-1"
            aria-label={t("pages:sessions.sessionCard.videoCount", {
              count: session.videoCount,
            })}
            title={t("pages:sessions.sessionCard.videoCount", {
              count: session.videoCount,
            })}
          >
            <Video className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
            {session.videoCount}
          </span>
        )}
        {session.description && (
          <>
            <span className="text-secondary-400">·</span>
            <span className="truncate max-w-xs">{session.description}</span>
          </>
        )}
      </div>
    </Link>
  );
}
